import {DealerPrice, VEHICLE_CATEGORIES, VehicleCategory} from "../../../model/brand/DealerPrice";

export interface DealerPriceRow {
	year: number | null;
	cells: { [category: string]: string };
}

export interface DealerPriceMatrixState {
	rows: DealerPriceRow[];
}

export function createEmptyMatrixState(): DealerPriceMatrixState {
	return {rows: [{year: null, cells: {}}]};
}

export function fromDealerPrices(prices: DealerPrice[]): DealerPriceMatrixState {
	const rowMap: { [key: string]: DealerPriceRow } = {};
	rowMap['null'] = {year: null, cells: {}};

	prices.forEach(p => {
		const key = p.registrationYear !== null ? String(p.registrationYear) : 'null';
		if (!rowMap[key]) {
			rowMap[key] = {year: p.registrationYear, cells: {}};
		}
		rowMap[key].cells[p.vehicleCategory] = (p.pricePennies / 100).toFixed(2);
	});

	const rows = Object.keys(rowMap)
		.sort((a, b) => {
			if (a === 'null') { return -1; }
			if (b === 'null') { return 1; }
			return Number(a) - Number(b);
		})
		.map(key => rowMap[key]);

	return {rows};
}

export function toDealerPrices(state: DealerPriceMatrixState): DealerPrice[] {
	const result: DealerPrice[] = [];
	state.rows.forEach(row => {
		VEHICLE_CATEGORIES.forEach(cat => {
			const val = row.cells[cat];
			if (val !== undefined && val.trim() !== '') {
				result.push({
					vehicleCategory: cat,
					registrationYear: row.year,
					pricePennies: Math.round(Number(val) * 100)
				});
			}
		});
	});
	return result;
}

export function hasAtLeastOneFilledCell(state: DealerPriceMatrixState): boolean {
	return state.rows.some(row =>
		VEHICLE_CATEGORIES.some(cat => {
			const val = row.cells[cat];
			return val !== undefined && val.trim() !== '';
		})
	);
}

export function isCellValueValid(value: string, maxValue: number = 99999.99): boolean {
	if (value.trim() === '') { return true; }
	const num = Number(value);
	return !isNaN(num) && num >= 0 && num <= maxValue;
}

export function isMatrixValid(state: DealerPriceMatrixState, maxCellValue?: number): boolean {
	if (!hasAtLeastOneFilledCell(state)) { return false; }
	return state.rows.every(row =>
		VEHICLE_CATEGORIES.every(cat => isCellValueValid(row.cells[cat] || '', maxCellValue))
	);
}

export function isYearValid(year: number): boolean {
	return Number.isInteger(year) && year >= 2019 && year <= 2099;
}

export function isDuplicateYear(state: DealerPriceMatrixState, year: number): boolean {
	return state.rows.some(row => row.year === year);
}

export function addYearRow(state: DealerPriceMatrixState, year: number): DealerPriceMatrixState {
	const rows = state.rows.concat([{year, cells: {}}]);
	rows.sort((a, b) => {
		if (a.year === null) { return -1; }
		if (b.year === null) { return 1; }
		return a.year - b.year;
	});
	return {rows};
}

export function removeYearRow(state: DealerPriceMatrixState, year: number): DealerPriceMatrixState {
	return {rows: state.rows.filter(row => row.year !== year)};
}

export function getInheritedCellValue(
	inherited: DealerPriceMatrixState,
	year: number | null,
	category: VehicleCategory
): string | undefined {
	const exactRow = inherited.rows.find(r => r.year === year);
	if (exactRow) {
		const val = exactRow.cells[category];
		if (val && val.trim() !== '') { return val; }
	}
	const catchAllRow = inherited.rows.find(r => r.year === null);
	if (catchAllRow) {
		const val = catchAllRow.cells[category];
		if (val && val.trim() !== '') { return val; }
	}
	return undefined;
}

export function fromCreditAmounts(prices: DealerPrice[]): DealerPriceMatrixState {
	const rowMap: { [key: string]: DealerPriceRow } = {};
	rowMap['null'] = {year: null, cells: {}};

	prices.forEach(p => {
		const key = p.registrationYear !== null ? String(p.registrationYear) : 'null';
		if (!rowMap[key]) {
			rowMap[key] = {year: p.registrationYear, cells: {}};
		}
		rowMap[key].cells[p.vehicleCategory] = String(p.pricePennies);
	});

	const rows = Object.keys(rowMap)
		.sort((a, b) => {
			if (a === 'null') { return -1; }
			if (b === 'null') { return 1; }
			return Number(a) - Number(b);
		})
		.map(key => rowMap[key]);

	return {rows};
}

export function toCreditAmounts(state: DealerPriceMatrixState): DealerPrice[] {
	const result: DealerPrice[] = [];
	state.rows.forEach(row => {
		VEHICLE_CATEGORIES.forEach(cat => {
			const val = row.cells[cat];
			if (val !== undefined && val.trim() !== '') {
				result.push({
					vehicleCategory: cat,
					registrationYear: row.year,
					pricePennies: Math.round(Number(val))
				});
			}
		});
	});
	return result;
}

export function updateCell(
	state: DealerPriceMatrixState,
	year: number | null,
	category: VehicleCategory,
	value: string
): DealerPriceMatrixState {
	return {
		rows: state.rows.map(row => {
			if (row.year !== year) { return row; }
			const newCells: { [cat: string]: string } = {};
			Object.keys(row.cells).forEach(k => { newCells[k] = row.cells[k]; });
			newCells[category] = value;
			return {year: row.year, cells: newCells};
		})
	};
}
